import { useCerbos } from '@cerbos/react';
import { useMemo, useState } from 'react';
import useDeepCompareEffect from 'react-use/esm/useDeepCompareEffect';
import useNetworkState from 'react-use/esm/useNetworkState';
import type { CheckResourcesResponse, ResourceCheck } from '@cerbos/core';

import { checkResourcesCache, getCheckResourcesKey } from './check-cache';
import { getPrincipalKey } from './principal-key';
import type { CerbosIsAllowed } from './types';

interface UseCerbosCheckResourcesArgs {
  resources?: ResourceCheck[];
  id?: string;
  /**
   * Reuse the decision already made for this principal and these exact checks
   * instead of asking the PDP again on every mount. Off by default: a cached
   * decision is only refreshed when the principal changes (see
   * `getPrincipalKey` for exactly which of its fields are keyed), on sign-out,
   * or once it is older than `CACHE_TTL_MS`, so a check that gates a
   * destructive action is better left live. Turn it on for checks that gate
   * *rendering*, where re-asking is what users see as a flicker — see the
   * flicker note below.
   *
   * The cache is shared and keyed on the principal and the checks alone, not
   * on the client that answered: it assumes the app's single PDP. See
   * `checkResourcesCache` before opting in a check aimed at a different one.
   */
  cache?: boolean;
}

/**
 * Principal key stood in for an uncached check, or one running without a
 * client. Nothing is ever written to the cache under it, so a read misses.
 */
const NO_PRINCIPAL_KEY = '';

interface UseCerbosCheckIsAllowedOptions {
  skip?: boolean;
}

/**
 * Fetches a batch of Cerbos resource checks and tracks loading/error state.
 *
 * Every check is a live request by default — neither `@cerbos/core` nor
 * `@cerbos/http` caches responses. Re-runs when the client, online status,
 * resources or id change (deep comparison).
 *
 * Pass `cache: true` to opt a check into the shared decision cache. That is a
 * rendering fix, not only a request-volume one (SKY-4579): a permission that
 * reads `false` for one paint and flips to `true` when the request lands makes
 * whatever it gates visibly blink — an account name renders redacted and then
 * re-renders in full, a tab disappears and comes back. Seeding state from the
 * cache means every mount after the first paints the final answer straight
 * away, and concurrent mounts asking the same question (one per row of a list)
 * share a single request instead of each firing their own.
 *
 * With `cache: true` an unmount detaches this hook from the request but does
 * not abort it: the request is shared with every other hook waiting on the same
 * decision, and letting it settle fills the cache for the next mount. Uncached
 * checks keep aborting on unmount as before.
 */
export function useCerbosCheckResources({
  resources,
  id,
  cache = false,
}: UseCerbosCheckResourcesArgs) {
  const client = useCerbos();
  const { online } = useNetworkState();

  // Only a cached check reads this — the uncached majority of this hook's
  // callers would otherwise rebuild a key nothing looks at on every render.
  //
  // `client` is guarded to mirror the effect below, which has always guarded
  // it. In practice it is never falsy — `useCerbos()` throws outright when
  // there is no `CerbosProvider` above — but reading `.principal` during
  // render should not be the thing that decides that.
  //
  // `CerbosProvider` re-derives its client only when the principal *deep*
  // changes (it runs the principal through `useDeepCompareMemoize`), so this
  // recomputes on a real principal change rather than on every render — and a
  // changed principal always reaches the key, which is what keeps one user's
  // decisions away from the next.
  const principalKey = useMemo(
    () => (cache && client ? getPrincipalKey(client.principal) : NO_PRINCIPAL_KEY),
    [cache, client],
  );

  // The question this hook is asking, or `undefined` when there is nothing to
  // ask. Built for uncached checks too: it is what drops a stale answer below.
  const requestKey =
    resources && resources.length > 0 && id ? getCheckResourcesKey(resources) : undefined;
  // The same question as the cache key it is stored under, or `undefined` when
  // it is not cached (or there is nothing to ask it with).
  const cacheKey = cache && client ? requestKey : undefined;

  const readCached = (): CheckResourcesResponse | undefined =>
    cacheKey === undefined ? undefined : checkResourcesCache.read(principalKey, cacheKey);

  // Read during render: serving the decision as the *initial* state is what
  // removes the blink, and a cache fill that lands later reaches this hook
  // through the shared request below instead.
  const [data, setData] = useState<CheckResourcesResponse | undefined>(readCached);
  // Only the value from `data`'s first render is used, which is the seeded one.
  const [isLoading, setIsLoading] = useState(data === undefined);
  const [error, setError] = useState<Error>();

  // Both halves of the key can change while this component stays mounted: the
  // principal (a tenant switch, or signing in as someone else) and the checks
  // themselves (a hook that re-asks for the row it is now rendering). Either
  // way `data` holds the answer to the *previous* question, so re-seed here.
  // Doing it in the effect below would paint one frame of that answer first —
  // permissions from the old principal, or one entity's decision under the
  // next entity's name — which is the very thing this cache exists to avoid.
  //
  // Uncached checks need the same reset (SKY-4797): without it a re-check keeps
  // reporting the previous decision with `isLoading: false` until it lands —
  // e.g. a delete button left enabled for a post that just left DRAFT.
  //
  // Keyed on the request key rather than on `resources`, which is rebuilt on
  // every render by every caller: identity would re-seed continuously, and
  // deep-comparing here would repeat work `getCheckResourcesKey` already did.
  // That costs one canonical-JSON pass per render for uncached checks too.
  //
  // `id` stays out of the key: it is never sent, only gates whether to ask,
  // and `requestKey` already encodes that gate. Keying on it would drop a
  // still-valid answer when a caller changes `id` over identical checks.
  //
  // Offline, a changed question reports loading until connectivity returns —
  // the same as a mount while offline, and failing closed beats a stale answer.
  const seedKey = `${principalKey}\u0000${requestKey ?? ''}`;
  const [seededFor, setSeededFor] = useState(seedKey);
  if (seededFor !== seedKey) {
    const reseeded = readCached();
    setSeededFor(seedKey);
    setData(reseeded);
    setIsLoading(reseeded === undefined && requestKey !== undefined);
    setError(undefined);
  }

  useDeepCompareEffect(() => {
    if (!resources || resources.length === 0) {
      setIsLoading(false);
      setData(undefined);
      setError(undefined);
      return;
    }

    if (client && online && id) {
      if (cache && cacheKey) {
        const hit = checkResourcesCache.read(principalKey, cacheKey);

        // Covers a cached decision under freshly changed args; a cache hit on
        // mount is already served by the initial state above, with no loading
        // render in between.
        if (hit) {
          setIsLoading(false);
          setData(hit);
          setError(undefined);
          return undefined;
        }

        let detached = false;

        void checkResourcesCache
          .request(principalKey, cacheKey, async () => client.checkResources({ resources }))
          .then((response) => {
            if (!detached) {
              setIsLoading(false);
              setData(response);
              setError(undefined);
            }
          })
          .catch((err: unknown) => {
            if (!detached) {
              setIsLoading(false);
              setData(undefined);
              setError(
                err instanceof Error
                  ? err
                  : new Error('An unexpected error occurred', { cause: err }),
              );
            }
          });

        return (): void => {
          detached = true;
        };
      }

      const abortController = new AbortController();

      client
        .checkResources({ resources }, { signal: abortController.signal })
        .then((response) => {
          if (!abortController.signal.aborted) {
            setIsLoading(false);
            setData(response);
            setError(undefined);
          }
        })
        .catch((err: unknown) => {
          if (!abortController.signal.aborted) {
            setIsLoading(false);
            setData(undefined);
            setError(
              err instanceof Error
                ? err
                : new Error('An unexpected error occurred', { cause: err }),
            );
          }
        });

      return (): void => {
        abortController.abort();
      };
    }
    return undefined;
  }, [client, online, resources, id, cache, principalKey]);

  return {
    isLoading,
    data,
    error,
  };
}

/**
 * Resolves a single Cerbos `isAllowed` check and tracks loading/error state.
 *
 * Pass `{ skip: true }` to suppress the request (e.g. when a required id is not yet available).
 * While skipped, `isLoading` is `false` and `data` is `undefined`.
 */
export function useCerbosCheckIsAllowed<
  TKind extends string = string,
  TAction extends string = string,
>(request: CerbosIsAllowed<TKind, TAction>, { skip = false }: UseCerbosCheckIsAllowedOptions = {}) {
  const client = useCerbos();
  const { online } = useNetworkState();
  const [isLoading, setIsLoading] = useState(!skip);
  const [data, setData] = useState<boolean | undefined>(undefined);
  const [error, setError] = useState<Error>();

  useDeepCompareEffect(() => {
    if (skip) {
      setIsLoading(false);
      setData(undefined);
      setError(undefined);
      return;
    }

    if (client && online && request) {
      const abortController = new AbortController();

      client
        .isAllowed(request, { signal: abortController.signal })
        .then((allowed) => {
          if (!abortController.signal.aborted) {
            setIsLoading(false);
            setData(allowed);
            setError(undefined);
          }
        })
        .catch((err: unknown) => {
          if (!abortController.signal.aborted) {
            setIsLoading(false);
            setData(undefined);
            setError(
              err instanceof Error
                ? err
                : new Error('An unexpected error occurred', { cause: err }),
            );
          }
        });

      return (): void => {
        abortController.abort();
      };
    }
    return undefined;
  }, [client, online, request, skip]);

  return {
    isLoading,
    data,
    error,
  };
}
